import * as React from "react";
import { toast } from "react-toastify";
import { CellInfo } from "react-table";
import styled, { css } from "styled-components";

import { ITableInstance } from "src/library/components/Tables/Table";
import { Table } from "src/library/components/Tables";
import { Text } from "src/library/components/Text/Text";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import Grid from "src/library/components/Hedron";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import {
  ISupportTicket,
  useSupportApi
} from "src/modules/support/api/support.api";
import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/core/shared/api";
import { customerSupportTicketsColumns } from "src/core/shared/components/tables";
import { useDrop } from "src/core/drops/hooks";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

/**
 * Support Tickets overview page
 * Renders support tickets by branch
 */

const ViewCustomerSupportTicketsPage: React.ReactFCWithChildren<PageProps<unknown>> = ({ title, ...props }) => {
  const TableRef = React.useRef<ITableInstance<ISupportTicket>>(null);
  const [ isLoading, setIsLoading ] = React.useState(false);
  const [ supportTickets, setSupportTickets ] = React.useState<PaginatedData<ISupportTicket>>();
  const [ listSupportTicketsRes, listSupportTicketsReq ] = useSupportApi("LIST_BRANCH_TICKETS");
  const [ { drop } ] = useDrop();
  const checkPermission = useCheckUserPermission();

  /**
   * Table actions
   */
  const tableActions = React.useCallback((row: CellInfo) => {
    const actions = [];

    if (!actions.length) {
      actions.push({
        title: "View",
        action: () => props.history.push(`/support/tickets/${row.original.id}`)
      });
    }

    return actions;
  }, [ props.history ]);

  /**
   * Fetches table data
   */

  const onFetchData = React.useCallback((filters: FilterParams<ISupportTicket>, pagination: PaginatedParams<ISupportTicket>) => {
    // Set loading to true
    setIsLoading(true);

    if (drop && drop.id) {
    // Fetch support tickets
      listSupportTicketsReq({
        branchId: drop.id,
        pagination,
        filters
      });
    }
  }, [ listSupportTicketsReq, drop ]);

  // listSupportTicketsRes change
  React.useEffect(() => {
    // If an error occurs show a toast
    if (listSupportTicketsRes.error) {
      setIsLoading(false);
      toast.error(listSupportTicketsRes.error.message);
    }

    // On success
    if (listSupportTicketsRes.data) {
      setIsLoading(false);
      setSupportTickets(listSupportTicketsRes.data);
    }
  }, [ listSupportTicketsRes ]);

  return (
    <Layout
      title={title + " Tickets"}
      {...props}
    >
      <MainContentWrapper fill>
        <ButtonLabelContainer>
          <ButtonWithLabel direction="vertical">
            <Text className="button-label">
              Request help about something
            </Text>

            <Button
              disabled={!checkPermission("amend-order")}
              onClick={() => props.history.push("tickets/raise-ticket", { cleanStart: true })}
            >
              Raise support ticket
            </Button>

          </ButtonWithLabel>
        </ButtonLabelContainer>

        <Table
          tableId="supportTickets"
          ref={TableRef}
          loading={isLoading}
          tableOf="Support Tickets"
          view="list"
          data={supportTickets ? supportTickets.result : []}
          onFetchData={onFetchData}
          pages={supportTickets ? supportTickets.pages : 0}
          totalRows={supportTickets ? supportTickets.count : 0}
          columns={customerSupportTicketsColumns({
            ActionButton: row => {
              const actions = tableActions(row);

              return actions.length ? (
                <ActionButton
                  title="actions"
                  items={tableActions(row)}
                />
              ) : null;
            }
          })}
        />
      </MainContentWrapper>
    </Layout>
  );
};

const ButtonLabelContainer = styled(Grid.Bounds)`
  justify-content: flex-start;
`;

const ButtonWithLabel = styled(Grid.Bounds)`
  align-items: center;
  justify-content: center;
  
  ${({ theme }) => css`
    .button-label {
      margin-bottom:  ${theme.spacing.margins.sm};
      align-self: center;
    }
  `}
`;

export default ViewCustomerSupportTicketsPage;
